import { ViewMode } from "../domain";

/**
 * Maps the short, URL-friendly `?lens=` token to the internal {@link ViewMode}.
 * This is the human-shareable form (`view | diff | timeline | code`) that sits
 * alongside the compressed `?state=` blob; it lets people deep-link a lens
 * without encoding full state. Unknown / missing tokens fall back to VIEW so a
 * malformed link can never strand the user on a blank screen.
 */
const LENS_TO_VIEW_MODE: Record<string, ViewMode> = {
  view: ViewMode.VIEW,
  diff: ViewMode.DIFF,
  timeline: ViewMode.TIMELINE,
  code: ViewMode.CODE,
};

const VIEW_MODE_TO_LENS: Record<ViewMode, string> = {
  [ViewMode.VIEW]: "view",
  [ViewMode.DIFF]: "diff",
  [ViewMode.TIMELINE]: "timeline",
  [ViewMode.CODE]: "code",
};

/** Parses a `?lens=` token into a ViewMode, defaulting to VIEW. */
export const parseLensParam = (value: string | null | undefined): ViewMode => {
  if (!value) return ViewMode.VIEW;
  return LENS_TO_VIEW_MODE[value.trim().toLowerCase()] ?? ViewMode.VIEW;
};

/** True when the token names a lens this app actually supports. */
export const isKnownLens = (value: string | null | undefined): boolean =>
  !!value && value.trim().toLowerCase() in LENS_TO_VIEW_MODE;

/** Inverse of {@link parseLensParam} — the short token for a ViewMode. */
export const lensParamForViewMode = (mode: ViewMode): string =>
  VIEW_MODE_TO_LENS[mode] ?? "view";
